import {
  FC,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
  PropsWithChildren,
  useMemo,
} from 'react';
import { RewriteEnabledMode, StrategyOverviewContextType } from './types';
import { StrategyOverviewContext } from './contexts';
import { QuillDeltaToHtmlConverter } from 'quill-delta-to-html';
import ReactQuill from 'react-quill';
import { createPortal } from 'react-dom';
import InsightsCard, {
  RewriteSubmittedContext,
} from 'pages/accountDrawer/emailView/insightsCard';
import { useAnalytics } from 'context/analytics';
import useRewriteSequenceInstance from 'hooks/services/assistant/useRewriteSequenceInstance';
import { RewriteSequenceInstanceInput } from 'crono-fe-common/types/DTO/rewriteSequenceInstanceInput';
import useRevertRewrittenSequenceInstance from 'hooks/services/assistant/useRevertRewrittenSequenceInstance';
import { RevertRewrittenSequenceInstanceInput } from 'crono-fe-common/types/DTO/revertRewrittenSequenceInstanceInput';
import { useConditionalSnackBar } from '../snackbar';
import { getError } from 'crono-fe-common/utils';
import useGetSequenceInstance from 'hooks/services/strategy/useGetSequenceInstance';
import { Prospect } from 'crono-fe-common/types/prospect';
import useGetProspect from '../../hooks/services/prospect/useGetProspect';
import { useNavigate } from 'react-router-dom';
import PATH from 'crono-fe-common/constants/path';
import useGetApplySequenceChanges from 'hooks/applySequenceChanges/useGetApplySequenceChanges';
import { useQueryClient } from '@tanstack/react-query';
import { useBackgroundInvalidateAutomatorDelay } from 'crono-fe-common/hooks/crono-extension/gateway';
import { invalidateApplyChangesToSequenceInstance } from 'hooks/services/strategy/useApplyChangesToSequenceInstances';
import { useInvalidateGetStrategy } from 'hooks/services/strategy/useGetStrategy';
import { useLeadScoreAccess } from 'pages/leadScore/hooks/useLeadScoreAccess';

const StrategyOverviewProvider: FC<PropsWithChildren> = ({ children }) => {
  const [prospect, setProspect] = useState<Prospect | null>(null);
  const [prospectId, setProspectId] = useState<string | null>(null);
  const [selectedOnBehalf, setSelectedOnBehalf] = useState<boolean>(false);
  const [selectedSequenceNotOwned, setSelectedSequenceNotOwned] =
    useState<boolean>(false);

  const selectProspect = useCallback(
    (
      nextProspectId: string | null,
      options?: { onBehalf?: boolean; sequenceNotOwned?: boolean },
    ) => {
      setProspectId(nextProspectId);
      setSelectedOnBehalf(!!options?.onBehalf);
      setSelectedSequenceNotOwned(!!options?.sequenceNotOwned);
    },
    [],
  );

  const { shouldShowScoreForProspect } = useLeadScoreAccess();

  const shouldShowScore = shouldShowScoreForProspect(prospect);

  const { data: prospectData } = useGetProspect(
    prospectId,
    false,
    true,
    true,
    shouldShowScore,
  );
  useEffect(() => {
    if (prospectData?.data?.data) {
      const prospectToSet = prospectData.data.data;
      prospectToSet.sequenceInstanceId = prospectToSet?.sequenceInstance?.id;
      setProspect(prospectToSet);
    }
  }, [prospectData]);
  /**
   * `prospect` lags `prospectId` by a render, so without this the sequence of
   * the previously selected contact would be refetched under the new
   * selection's on-behalf flag — a guaranteed 403 on every switch from a
   * non-owned contact to an owned one.
   */
  const isProspectCurrent = !!prospect && prospect.objectId === prospectId;

  const { data: sequenceInstanceResponse } = useGetSequenceInstance(
    isProspectCurrent ? (prospect?.sequenceInstance?.id ?? 0) : 0,
    prospect?.objectId ?? '',
    true,
    undefined,
    selectedOnBehalf,
  );

  const insightsCardRef = useRef<HTMLDivElement | null>(null);
  const insightsButtonRef = useRef<HTMLButtonElement | null>(null);
  const [editorInFocus, setEditorInFocus] = useState<ReactQuill | null>(null);
  const [showInsightTab, setShowInsightTab] = useState<boolean>(false);
  const [rewriteEnabledMode, setRewriteEnabledMode] =
    useState<RewriteEnabledMode | null>(null);
  const [selectedText, setSelectedText] = useState<string>();
  const [wholeMessage, setWholeMessage] = useState<string>();
  const [textAreaSelection, setTextAreaSelection] = useState({
    start: 0,
    length: 0,
  });

  const handleChangeSelection = (
    range: { index: number; length: number } | null,
  ) => {
    const quill = editorInFocus?.getEditor();
    if (quill && range && range.length > 0) {
      const selectedDelta = quill.getContents(range.index, range.length);
      const converter = new QuillDeltaToHtmlConverter(selectedDelta.ops ?? [], {
        inlineStyles: true,
      });
      const selectedHtml = converter.convert();
      setTextAreaSelection({ start: range.index, length: range.length });
      setSelectedText(selectedHtml);
    } else {
      const totalLength = quill?.getLength() ?? 0;
      setTextAreaSelection({ start: 0, length: totalLength });
      setSelectedText(quill?.root?.innerHTML ?? '');
    }
    setWholeMessage(quill?.root?.innerHTML ?? '');
  };

  useEffect(() => {
    const quill = editorInFocus?.getEditor();
    if (quill) {
      quill.on('selection-change', handleChangeSelection);
      handleChangeSelection(quill.getSelection());
    }
    return () => {
      quill?.off('selection-change', handleChangeSelection);
    };
  }, [editorInFocus]);

  const showSelection = () => {
    const editor = editorInFocus?.getEditor();
    if (!editor) return;

    editor.setSelection(textAreaSelection.start, textAreaSelection.length);
  };

  const replaceSelectedText = (html: string) => {
    const editor = editorInFocus?.getEditor();
    if (editor) {
      editor.deleteText(textAreaSelection.start, textAreaSelection.length);
      editor.clipboard.dangerouslyPasteHTML(
        textAreaSelection.start,
        html,
        'user',
      );
    }
  };

  const analytics = useAnalytics();

  const [rewriteSubmittedContext, setRewriteSubmittedContext] =
    useState<RewriteSubmittedContext>();

  const trackTemplateSaveIfNeeded = (templateText: string) => {
    if (rewriteSubmittedContext && analytics) {
      analytics.track('sent-with-rewrite', {
        ...rewriteSubmittedContext,
        actualMessage: templateText,
      });
    }
    setRewriteSubmittedContext(undefined);
  };

  const closeInsightCard = () => {
    setRewriteEnabledMode(null);
    setShowInsightTab(false);
  };

  const resetInsightsContext = () => {
    setSelectedText(undefined);
    setWholeMessage(undefined);
    setTextAreaSelection({ start: 0, length: 0 });
    setShowInsightTab(false);
    setRewriteEnabledMode(null);
    setProspect(null);
    setProspectId(null);
    setSelectedOnBehalf(false);
    setSelectedSequenceNotOwned(false);
    setEditorInFocus(null);
  };

  const navigate = useNavigate();

  const handleRedirectToHome = () => {
    navigate(PATH.LISTS);
  };

  const handleRedirectToTasks = useCallback(() => {
    navigate(PATH.TASKS_MAIN_PAGE);
  }, []);

  const {
    mutateAsync: rewriteSequenceInstanceAsync,
    isLoading: isRewriteSequenceInstanceInProgress,
    isSuccess: isRewriteSequenceInstanceSuccess,
    error: rewriteSequenceInstanceError,
  } = useRewriteSequenceInstance();

  const invalidateGetStrategy = useInvalidateGetStrategy();

  const rewriteSequenceInstance = async (
    request: RewriteSequenceInstanceInput,
  ) => {
    await rewriteSequenceInstanceAsync(request);
    invalidateGetStrategy();
  };

  const {
    mutateAsync: revertRewrittenSequenceInstanceAsync,
    isLoading: isRevertInProgress,
    isSuccess: isRevertSuccess,
    error: revertError,
  } = useRevertRewrittenSequenceInstance();

  const revertRewrittenSequenceInstance = async (
    request: RevertRewrittenSequenceInstanceInput,
  ) => {
    await revertRewrittenSequenceInstanceAsync(request);
    invalidateGetStrategy();
  };

  useConditionalSnackBar([
    {
      condition: !!rewriteSequenceInstanceError,
      message:
        getError(rewriteSequenceInstanceError) ??
        'Error during rewrite process',
      severity: 'error',
    },
    {
      condition: isRewriteSequenceInstanceSuccess,
      message: 'The sequence was successfully rewritten',
      severity: 'success',
    },
    {
      condition: !!revertError,
      message: getError(revertError) ?? 'Error during revert process',
      severity: 'error',
    },
    {
      condition: isRevertSuccess,
      message: 'All templates were successfully reverted',
      severity: 'success',
    },
  ]);

  const [enable, setEnable] = useState<boolean>(true);
  const startFetchingActiveSequenceChanges = () => {
    setEnable(true);
  };

  const {
    data: allActiveSequenceChanges,
    isLoading,
    isRefetching,
  } = useGetApplySequenceChanges(enable);

  //We decided not to show in progress changes that are going for more than 5 minutes
  const sequenceChangesShorterThan5Minutes = useMemo(() => {
    return (
      allActiveSequenceChanges?.filter(
        (sc) =>
          new Date().getTime() - new Date(sc.startDate).getTime() <
          5 * 60 * 1000,
      ) ?? []
    );
  }, [allActiveSequenceChanges]);

  const queryClient = useQueryClient();
  const { call: invalidateAutomatorDelay } =
    useBackgroundInvalidateAutomatorDelay();

  useEffect(() => {
    if (
      !isLoading &&
      !isRefetching &&
      sequenceChangesShorterThan5Minutes.length === 0
    ) {
      setEnable(false);
      //When apply is done I invalidate the cache to retrieve the correct data (since they may be blocked to the previous version since cached)
      invalidateApplyChangesToSequenceInstance(
        queryClient,
        invalidateAutomatorDelay,
      );
    }
  }, [sequenceChangesShorterThan5Minutes, isLoading, isRefetching]);

  return (
    <StrategyOverviewContext.Provider
      value={{
        startFetchingActiveSequenceChanges,
        sequenceChangesShorterThan5Minutes,
        handleRedirectToHome,
        handleRedirectToTasks,
        insightsContext: {
          insightsCardRef,
          insightsButtonRef,
          prospect: prospectId ? prospect : null,
          selectProspect,
          selectedOnBehalf,
          selectedSequenceNotOwned,
          showInsightTab,
          setShowInsightTab,
          rewriteEnabledMode,
          setRewriteEnabledMode,
          editorInFocus,
          setEditorInFocus,
          resetInsightsContext,
          trackTemplateSaveIfNeeded,
          setRewriteSubmittedContext,
          rewriteSequenceInstance,
          isRewriteSequenceInstanceInProgress,
          revertRewrittenSequenceInstance,
          isRevertInProgress,
          sequenceInstance: sequenceInstanceResponse?.data?.data ?? null,
        },
      }}
    >
      {showInsightTab &&
        createPortal(
          <InsightsCard
            close={closeInsightCard}
            prospect={prospect}
            account={prospect?.account ?? null}
            wholeMessage={wholeMessage}
            partToRewrite={selectedText}
            showSelection={showSelection}
            replaceSelectedText={replaceSelectedText}
            isRewriteResponseHtml={true}
            style={{ right: 0 }}
            setRewriteSubmittedContext={setRewriteSubmittedContext}
            isDraggable
            title={rewriteEnabledMode != null ? 'Rewrite with AI' : 'Insights'}
          />,
          document.body,
        )}
      {children}
    </StrategyOverviewContext.Provider>
  );
};

export function useShortStrategyOverviewContext(): Exclude<
  StrategyOverviewContextType,
  'insightsContext'
> {
  const context = useContext(StrategyOverviewContext);
  if (context === undefined) {
    throw new Error(
      `useShortStrategyOverviewContext must be used within a StrategyOverviewProvider`,
    );
  }

  return context;
}

export function useSafeStrategyOverviewContext(): StrategyOverviewContextType {
  const context = useContext(StrategyOverviewContext);
  if (context === undefined) {
    // fallback for strategy detail tabs that don't need insights but has <EditableTemplate />
    return {
      startFetchingActiveSequenceChanges: () => {},
      sequenceChangesShorterThan5Minutes: [],
      handleRedirectToHome: () => {},
      handleRedirectToTasks: () => {},
      insightsContext: {
        insightsCardRef: { current: null },
        insightsButtonRef: { current: null },
        prospect: null,
        selectProspect: () => {},
        selectedOnBehalf: false,
        selectedSequenceNotOwned: false,
        showInsightTab: false,
        setShowInsightTab: () => {},
        rewriteEnabledMode: null,
        setRewriteEnabledMode: () => {},
        editorInFocus: null,
        setEditorInFocus: () => {},
        resetInsightsContext: () => {},
        trackTemplateSaveIfNeeded: () => {},
        setRewriteSubmittedContext: () => {},
        rewriteSequenceInstance: () => Promise.resolve(),
        isRewriteSequenceInstanceInProgress: false,
        revertRewrittenSequenceInstance: () => Promise.resolve(),
        isRevertInProgress: false,
        sequenceInstance: null,
      },
    };
  }

  return context;
}

export default StrategyOverviewProvider;
